import { useCallback } from 'react';
import { useAnalyzeBuyersCsv, useImportBuyers } from '@tinct/core-api';
import {
  AnalyzeCsvResponse,
  CrmSegment,
  CsvMapping,
  ImportResponse,
  ReadCompanyRef,
} from '@tinct/core-api-models';

// Utility to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

export type AnalyzeCsvParams = {
  file: File;
};

export type ImportCsvParams = {
  fileBase64: string;
  mapping: CsvMapping;
};

export type ImportCompaniesParams = {
  companies: ReadCompanyRef[];
};

export type ImportCrmSegmentParams = {
  integrationId: string;
  segment: CrmSegment;
};

export type UseImportBuyerActionsReturn = {
  analyzeAction: {
    analyzeCsv: (
      params: AnalyzeCsvParams,
    ) => Promise<{ analysis: AnalyzeCsvResponse; base64: string } | undefined>;
    isAnalyzing: boolean;
  };
  importAction: {
    importCsv: (params: ImportCsvParams) => Promise<ImportResponse | undefined>;
    importCompanies: (params: ImportCompaniesParams) => Promise<ImportResponse | undefined>;
    importCrmSegment: (params: ImportCrmSegmentParams) => Promise<ImportResponse | undefined>;
    isImporting: boolean;
  };
};

// Buyers are scoped to the organization via the X-Tinct-OrganizationId header
// (auto-injected by the api client), so the import context carries no parent
// reference — unlike the audience flow which passes an audience context.
const BUYER_CONTEXT = {};

export const useImportBuyerActions = (): UseImportBuyerActionsReturn => {
  const { mutateAsync: analyzeCsvMutation, isPending: isAnalyzing } =
    useAnalyzeBuyersCsv();
  const { mutateAsync: importBuyersMutation, isPending: isImporting } =
    useImportBuyers();

  const analyzeCsv = useCallback(
    async (params: AnalyzeCsvParams) => {
      try {
        const base64 = await fileToBase64(params.file);
        const analysis = await analyzeCsvMutation({
          csv: { base64 },
          context: BUYER_CONTEXT,
        });
        return { analysis, base64 };
      } catch {
        return undefined;
      }
    },
    [analyzeCsvMutation],
  );

  const importCsv = useCallback(
    async (params: ImportCsvParams) => {
      try {
        return await importBuyersMutation({
          type: 'csv',
          context: BUYER_CONTEXT,
          csv: { base64: params.fileBase64 },
          mapping: params.mapping,
        });
      } catch {
        return undefined;
      }
    },
    [importBuyersMutation],
  );

  const importCompanies = useCallback(
    async (params: ImportCompaniesParams) => {
      try {
        return await importBuyersMutation({
          type: 'simple_list',
          context: BUYER_CONTEXT,
          list: params.companies.map((company) => ({
            company: { id: company.id },
          })),
        });
      } catch {
        return undefined;
      }
    },
    [importBuyersMutation],
  );

  const importCrmSegment = useCallback(
    async (params: ImportCrmSegmentParams) => {
      try {
        return await importBuyersMutation({
          type: 'crm_integration',
          context: BUYER_CONTEXT,
          crmIntegration: { id: params.integrationId },
          crmSegment: { id: params.segment.id! },
        });
      } catch {
        return undefined;
      }
    },
    [importBuyersMutation],
  );

  return {
    analyzeAction: {
      analyzeCsv,
      isAnalyzing,
    },
    importAction: {
      importCsv,
      importCompanies,
      importCrmSegment,
      isImporting,
    },
  };
};
